import React, { useState } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { Translations } from "../../../../core/api/models";
import ErrorMessage from "../../../../components/text/ErrorMessage";
import { DropdownList } from "react-widgets";
import classnames from "classnames";
import {
  AppPermissions,
  useHasPermission,
  VisibilityGroup,
  CreateVisibilityGroupFormData,
  User,
} from "../../../../core";
import { useDataLoader } from "react-remote-data-hooks";
import api from "../../../../api";
import EntityDropdown from "../../components/entity-dropdown";
import UserList from "../../components/UserList";

const CreateForm: React.FunctionComponent<{
  onSubmit: (data: CreateVisibilityGroupFormData) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
  data?: VisibilityGroup;
  error?: string;
  onDelete: (id?: string) => void;
  groups?: VisibilityGroup[];
}> = (props) => {
  const entitiesKeys = [
    {
      key: "lead",
      label: "Lead / Opportunities",
    },
    // {
    //   key: "configuration",
    //   label: "Requirement / Proposal / Agreement",
    // },
    {
      key: "activity",
      label: "Activities",
    },
    {
      key: "organization",
      label: "Company",
    },
    {
      key: "customer",
      label: "Customers",
    },
    // {
    //   key: "report",
    //   label: "Reports",
    // },
    // {
    //   key: "payment",
    //   label: "Payments",
    // },
  ];

  const { data: users, reload } = useDataLoader<User[]>(() =>
    props.data?._id
      ? api.visibilityGroup.getVsibilityGroupUsers(props.data?._id)
      : undefined
  );

  const canCreateVisiblityGroup = useHasPermission(
    AppPermissions.CreateVisiblityGroup
  );

  const canDeleteVisiblityGroup = useHasPermission(
    AppPermissions.DeleteVisiblityGroup
  );

  const [isDeleteModalOpen, setDeleteModalOpen] = React.useState<boolean>(
    false
  );

  const [entities, setEntities] = useState<
    VisibilityGroup["entities"] | undefined
  >(props?.data?.entities || []);
  const [currentTab, setCurrentTab] = useState<
    "generalInfo" | "models" | "exclusive"
  >("generalInfo");
  const {
    control,
    handleSubmit,
    errors,
    watch,
  } = useForm<CreateVisibilityGroupFormData>({
    reValidateMode: "onChange",
    mode: "onSubmit",
    defaultValues: {
      ...props.data,
    },
  });

  const onCancelDelete = () => {
    setDeleteModalOpen(false);
  };

  const parentGrups = props.groups?.filter((x) => x._id !== props?.data?._id);

  const renderDiscountTypeName = () => {
    return (
      <div className="col-12">
        <div className="form-group font-weight-semibold mb-4">
          <label className="font-sm text-uppercase">Group Name<span className="text-danger">*</span></label>
          <Controller
            control={control}
            name="title"
            rules={{ required: "Name is required" }}
            defaultValue={props.data?.title}
            render={({ onChange, name, value }) => {
              return (
                <input
                  type="text"
                  className="form-control rounded-sm font-weight-semibold"
                  value={value}
                  name={name}
                  onChange={onChange}
                />
              );
            }}
          ></Controller>
          <ErrorMessage>{errors.title?.message}</ErrorMessage>
        </div>
      </div>
    );
  };

  const renderArabicTextBox = () => {
    return (
      <div className="col-12">
        <div className="form-group font-weight-semibold mb-4">
          <label className="font-sm text-uppercase">Name In Arabic</label>
          <Controller
            control={control}
            // rules={{ required: 'Arabic name is required' }}
            name="translations.name.ar"
            defaultValue={props.data?.translations?.name?.ar}
            render={({ onChange, value, name }) => (
              <input
                type="text"
                className="form-control rounded-sm font-weight-semibold"
                id="arabicName"
                placeholder="Arabic Name"
                name={name}
                value={value}
                onChange={onChange}
              />
            )}
          />
          <ErrorMessage>{errors?.translations?.name?.ar?.message}</ErrorMessage>
        </div>
      </div>
    );
  };

  const renderParentId = () => {
    return (
      <div className="col-12">
        <div className="form-group font-weight-semibold mb-4">
          <label className="font-sm text-uppercase">Parent</label>
          <Controller
            control={control}
            name="parentId"
            defaultValue={props.data?.parentId}
            render={({ onChange, value, name }) => (
              <DropdownList
                value={value}
                defaultValue={value}
                data={parentGrups}
                textField={"title"}
                valueField={"_id"}
                placeholder={"Select Parent"}
                onChange={(val) => {
                  onChange(val._id);
                }}
              />
            )}
          />
          <ErrorMessage>{errors.parentId?.message}</ErrorMessage>
        </div>
      </div>
    );
  };

  const renderEntity = () => {
    return (
      <>
        {entitiesKeys.map((item) => {
          return (
            <div className="col-12">
              <div className="form-group font-weight-semibold mb-4">
                <label className="font-sm text-uppercase">{item.label}</label>

                <EntityDropdown
                  defaultValue={
                    entities?.find(
                      (e: { entity: string }) => e.entity === item?.key
                    )?.visibilityLevel
                  }
                  placeholder={"Select Visibility Level"}
                  onReset={() => {
                    const entity = entities ? [...entities] : [];

                    setEntities(
                      entity?.filter((x) => x.entity !== item?.key) || []
                    );
                  }}
                  onChange={(val) => {
                    const entity = entities ? [...entities] : [];

                    if (!!entity?.find((x) => x.entity === item?.key)) {
                      setEntities(
                        entity?.map((x) => {
                          return {
                            entity: x.entity,
                            visibilityLevel:
                              x.entity === item?.key ? val : x.visibilityLevel,
                          };
                        })
                      );
                    } else {
                      entity.push({
                        entity: item?.key,
                        visibilityLevel: val,
                      });

                      setEntities(
                        entity?.map((x) => {
                          return {
                            entity: x.entity,
                            visibilityLevel: x.visibilityLevel,
                          };
                        })
                      );
                    }
                  }}
                />
              </div>
            </div>
          );
        })}
      </>
    );
  };

  const renderGeneralInfoTab = () => {
    return (
      <div className="row gutter-10">
        {renderDiscountTypeName()}
        {!!parentGrups?.length && renderParentId()}
        {renderButton()}
      </div>
    );
  };

  const renderUserTab = () => {
    return (
      <>
        <UserList users={users} />
      </>
    );
  };

  const onUpdateEntity = (group: CreateVisibilityGroupFormData) => {
    props.onSubmit({
      ...group,
      entities,
    });
  };

  const onDelete = () => {
    setDeleteModalOpen(true);
  };

  const renderEntiryTab = () => {
    return (
      <div className="row gutter-10">
        {renderEntity()}
        <div className="col-12">
          <div className="form-group font-weight-semibold mb-4 text-right pt-3">
            <ErrorMessage>{props.error}</ErrorMessage>
            <br />
            <button
              className="btn btn-outline-dark py-2 font-weight-semibold font-normal px-4 mr-1"
              onClick={props.onCancel}
            >
              Cancel
            </button>
            {canCreateVisiblityGroup && (
              <button
                className={classnames({
                  "btn btn-primary py-2 font-weight-semibold font-normal px-4": true,
                })}
                onClick={handleSubmit(onUpdateEntity)}
                disabled={props.isSubmitting}
              >
                Update
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderDeleteConfirm = () => {
    return isDeleteModalOpen ? (
      <>
        <div
          className="modal fade show in"
          id="exampleModal"
          aria-labelledby="exampleModalLabel"
          aria-hidden="true"
        >
          <div className="modal-dialog modal-dialog-confirm modal-dialog-centered">
            <div className="modal-content rounded-0">
              <div className="modal-body text-center">
                <div className="container">
                  <div className="row">
                    <div className="col">
                      <p>
                        You are about to delete the visibility group{" "}
                        {props.data?.title}
                      </p>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-auto mr-auto">
                      <button
                        type="button"
                        className="btn btn-outline-dark py-2 font-weight-semibold font-normal px-4 mr-1"
                        onClick={onCancelDelete}
                      >
                        Cancel
                      </button>
                    </div>
                    <div className="col-auto">
                      <button
                        type="submit"
                        className="btn btn-primary py-2 font-weight-semibold font-normal px-4 "
                        onClick={() => {
                          props.onDelete(props.data?._id);
                          setDeleteModalOpen(false);
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="modal-backdrop fade show"></div>
      </>
    ) : (
      <></>
    );
  };

  const renderButton = () => {
    return (
      <div className="col-12">
        <div className="form-group font-weight-semibold mb-3 text-right pt-3">
          {props.data?._id && canDeleteVisiblityGroup && (
            <button
              type="button"
              className="btn btn-outline-gray py-2 font-weight-semibold font-normal px-4 mr-1"
              onClick={onDelete}
            >
              <i className="icon-bin-fill cursor-pointer align-self-center font-lg text-gray-700"></i>
            </button>
          )}
          <button
            type="button"
            className="btn btn-outline-dark py-2 font-weight-semibold font-normal px-4 mr-1"
            onClick={props.onCancel}
          >
            Cancel
          </button>
          {!props.data?._id && canCreateVisiblityGroup && (
            <button
              type="submit"
              className="btn btn-primary py-2 font-weight-semibold font-normal px-4 "
              disabled={props.isSubmitting}
              onClick={handleSubmit(props.onSubmit)}
            >
              Create
            </button>
          )}
          {props.data?._id && canCreateVisiblityGroup && (
            <button
              type="submit"
              className="btn btn-primary py-2 font-weight-semibold font-normal px-4 "
              onClick={handleSubmit(props.onSubmit)}
              disabled={props.isSubmitting}
            >
              Update
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="col-lg col-md-6 col-12 p-2 setting-detail-block left-0 top-0 bg-white w-100 h-100 zIndex-1">
      <div className="text-right d-md-none">
        <i className="icon-close"></i>
      </div>
      <div className="p-4">
        <>
          <h4 className="mb-3 font-weight-semibold">
            {props.data && props.data._id ? "Update " : "Create New "}{" "}
            Visibility Group
          </h4>

          <ul
            className="nav tab-underline mb-4 border-bottom font-xs font-weight-semibold col"
            id="activity-tab"
            role="tablist"
          >
            <li className="nav-item mr-4" role="presentation">
              <a
                className={classnames({
                  "nav-link position-relative px-0 py-2 text-gray-600": true,
                  active: currentTab === "generalInfo",
                })}
                id="note-tab"
                data-toggle="pill"
                href="#note"
                role="tab"
                aria-controls="note"
                aria-selected="true"
                onClick={() => setCurrentTab("generalInfo")}
              >
                <span className="py-1 d-inline-flex">General</span>
              </a>
            </li>
            {props.data && (
              <>
                <li className="nav-item mr-4" role="presentation">
                  <a
                    className={classnames({
                      "nav-link position-relative px-0 py-2 text-gray-600": true,
                      active: currentTab === "models",
                    })}
                    id="activity-tab"
                    data-toggle="pill"
                    href="#activity"
                    role="tab"
                    aria-controls="activity"
                    aria-selected="true"
                    onClick={() => setCurrentTab("models")}
                  >
                    <span className="py-1 d-inline-flex">
                      Who can see item created by user in this group
                    </span>
                  </a>
                </li>
                <li className="nav-item mr-4" role="presentation">
                  <a
                    className={classnames({
                      "nav-link position-relative px-0 py-2 text-gray-600": true,
                      active: currentTab === "exclusive",
                    })}
                    id="activity-tab"
                    data-toggle="pill"
                    href="#activity"
                    role="tab"
                    aria-controls="activity"
                    aria-selected="true"
                    onClick={() => setCurrentTab("exclusive")}
                  >
                    <span className="py-1 d-inline-flex">Users</span>
                  </a>
                </li>
              </>
            )}
          </ul>
          <div className="tab-content" id="activity-tabContent">
            <div
              className={classnames({
                "tab-pane fade": true,
                "show active": currentTab === "generalInfo",
              })}
              id="note"
              role="tabpanel"
              aria-labelledby="note-tab"
            >
              {renderGeneralInfoTab()}
            </div>
            <div
              className={classnames({
                "tab-pane fade": true,
                "show active": currentTab === "models",
              })}
              id="note"
              role="tabpanel"
              aria-labelledby="note-tab"
            >
              {renderEntiryTab()}
            </div>
            <div
              className={classnames({
                "tab-pane fade": true,
                "show active": currentTab === "exclusive",
              })}
              id="note"
              role="tabpanel"
              aria-labelledby="note-tab"
            >
              {renderUserTab()}
            </div>
          </div>
        </>

        {renderDeleteConfirm()}
      </div>
    </div>
  );
};

export default CreateForm;
